import type { Transaction, TransactionFormData } from '@/types/transaction';
import { HistoryAction, createTransactionAction } from '@/hooks/useTransactionHistory';

export interface LogEntry {
  id: string;
  timestamp: number;
  level: 'info' | 'warning' | 'error' | 'success';
  category: 'transaction' | 'user_action' | 'system' | 'notification' | 'error';
  action: string;
  description: string;
  details?: {
    transactionId?: string;
    transactionType?: string;
    amount?: number;
    category?: string;
    userId?: string;
    sessionId?: string;
    userAgent?: string;
    ip?: string;
    duration?: number;
    error?: string;
    metadata?: Record<string, any>;
  };
  stackTrace?: string;
  correlationId?: string;
}

export interface LogFilter {
  level?: LogEntry['level'][];
  category?: LogEntry['category'][];
  startDate?: Date;
  endDate?: Date;
  search?: string;
  transactionId?: string;
  userId?: string;
}

class ActionLogger {
  private logs: LogEntry[] = [];
  private maxLogSize = 1000; // Keep last 1000 log entries
  private storageKey = 'transaction-action-logs';
  private sessionId: string;

  constructor() {
    this.sessionId = this.generateSessionId();
    this.loadLogs();
    this.setupErrorHandling();
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private setupErrorHandling() {
    if (typeof window !== 'undefined') {
      window.addEventListener('error', (event) => {
        this.error('JavaScript Error', event.message, {
          category: 'error',
          details: {
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno,
            stackTrace: event.error?.stack,
          },
        });
      });

      window.addEventListener('unhandledrejection', (event) => {
        this.error('Unhandled Promise Rejection', event.reason, {
          category: 'error',
          details: {
            stackTrace: event.reason?.stack || event.reason,
          },
        });
      });
    }
  }

  private createLogEntry(
    level: LogEntry['level'],
    category: LogEntry['category'],
    action: string,
    description: string,
    details?: LogEntry['details']
  ): LogEntry {
    return {
      id: `log_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: Date.now(),
      level,
      category,
      action,
      description,
      details: {
        ...details,
        sessionId: this.sessionId,
        userAgent: typeof window !== 'undefined' ? window.navigator.userAgent : undefined,
      },
      correlationId: this.generateCorrelationId(),
    };
  }

  private generateCorrelationId(): string {
    return `corr_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private addLog(entry: LogEntry) {
    this.logs.unshift(entry);

    // Trim logs if they exceed max size
    if (this.logs.length > this.maxLogSize) {
      this.logs = this.logs.slice(0, this.maxLogSize);
    }

    // Save to localStorage
    this.saveLogs();

    // Emit event for real-time monitoring
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('action-log-updated', {
        detail: entry
      }));
    }
  }

  private loadLogs() {
    if (typeof window === 'undefined') return;

    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        this.logs = Array.isArray(parsed) ? parsed : parsed.logs || [];
      }
    } catch (error) {
      console.error('Error loading action logs:', error);
    }
  }

  private saveLogs() {
    if (typeof window === 'undefined') return;

    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.logs));
    } catch (error) {
      console.error('Error saving action logs:', error);
      // If localStorage is full, clear some old logs
      if (error.name === 'QuotaExceededError') {
        this.logs = this.logs.slice(0, Math.floor(this.maxLogSize / 2));
        try {
          localStorage.setItem(this.storageKey, JSON.stringify(this.logs));
        } catch (retryError) {
          console.error('Still cannot save logs after cleanup:', retryError);
        }
      }
    }
  }

  // Public logging methods
  info(action: string, description: string, options?: {
    category?: LogEntry['category'];
    details?: LogEntry['details'];
  }) {
    const entry = this.createLogEntry('info', options?.category || 'user_action', action, description, options?.details);
    this.addLog(entry);
  }

  warning(action: string, description: string, options?: {
    category?: LogEntry['category'];
    details?: LogEntry['details'];
  }) {
    const entry = this.createLogEntry('warning', options?.category || 'user_action', action, description, options?.details);
    this.addLog(entry);
  }

  error(action: string, description: string, options?: {
    category?: LogEntry['category'];
    details?: LogEntry['details'];
    stackTrace?: string;
  }) {
    const entry = this.createLogEntry('error', options?.category || 'error', action, description, {
      ...options?.details,
      stackTrace: options?.stackTrace,
    });
    this.addLog(entry);
  }

  success(action: string, description: string, options?: {
    category?: LogEntry['category'];
    details?: LogEntry['details'];
  }) {
    const entry = this.createLogEntry('success', options?.category || 'user_action', action, description, options?.details);
    this.addLog(entry);
  }

  // Transaction-specific logging methods
  logTransactionCreate(transaction: TransactionFormData | Transaction, duration?: number) {
    const amount = typeof transaction.amount === 'number' ? transaction.amount : 0;

    this.info('Transaction Created', `New ${transaction.type} transaction created`, {
      category: 'transaction',
      details: {
        transactionId: 'id' in transaction ? String(transaction.id) : undefined,
        transactionType: transaction.type,
        amount,
        category: 'category_id' in transaction ? String(transaction.category_id) : undefined,
        duration,
      },
    });
  }

  logTransactionUpdate(
    beforeState: Transaction,
    afterState: TransactionFormData | Transaction,
    duration?: number
  ) {
    const beforeAmount = beforeState.amount || 0;
    const afterAmount = typeof afterState.amount === 'number' ? afterState.amount : 0;
    const amountChanged = beforeAmount !== afterAmount;

    this.info('Transaction Updated', `Transaction ${beforeState.description} modified`, {
      category: 'transaction',
      details: {
        transactionId: String(beforeState.id),
        transactionType: beforeState.type,
        amountChanged,
        beforeAmount,
        afterAmount,
        duration,
      },
    });
  }

  logTransactionDelete(transaction: Transaction) {
    this.warning('Transaction Deleted', `Transaction ${transaction.description} removed`, {
      category: 'transaction',
      details: {
        transactionId: String(transaction.id),
        transactionType: transaction.type,
        amount: transaction.amount,
        category: String(transaction.category_id),
      },
    });
  }

  logTransactionError(action: string, transactionId?: string, error?: any) {
    this.error(`Transaction ${action}`, `Error during transaction ${action}`, {
      category: 'error',
      details: {
        transactionId,
        error: error?.message || String(error),
        stackTrace: error?.stack,
      },
      stackTrace: error?.stack,
    });
  }

  logNotification(type: 'email' | 'sms' | 'in_app', action: string, description: string, details?: any) {
    this.info(`Notification ${action}`, `${type} notification: ${description}`, {
      category: 'notification',
      details: {
        notificationType: type,
        ...details,
      },
    });
  }

  // History action logging
  logHistoryAction(historyAction: HistoryAction, success: boolean = true) {
    const level = success ? 'success' : 'error';
    const description = success
      ? `History action completed: ${historyAction.description}`
      : `History action failed: ${historyAction.description}`;

    this[level]('History Action', description, {
      category: 'user_action',
      details: {
        actionType: historyAction.type,
        transactionId: historyAction.transactionId,
        historyActionId: historyAction.id,
        timestamp: historyAction.timestamp,
      },
    });
  }

  // Performance logging
  logPerformance(action: string, startTime: number, additionalData?: any) {
    const duration = Date.now() - startTime;

    if (duration > 5000) {
      this.warning('Slow Operation', `${action} took ${duration}ms`, {
        category: 'system',
        details: {
          action,
          duration,
          ...additionalData,
        },
      });
    } else {
      this.info('Performance', `${action} completed in ${duration}ms`, {
        category: 'system',
        details: {
          action,
          duration,
          ...additionalData,
        },
      });
    }
  }

  // User behavior logging
  logUserInteraction(element: string, action: string, value?: any) {
    this.info('User Interaction', `User ${action} on ${element}`, {
      category: 'user_action',
      details: {
        element,
        action,
        value,
      },
    });
  }

  // Query methods
  getLogs(filter?: LogFilter): LogEntry[] {
    let filteredLogs = [...this.logs];

    if (filter) {
      if (filter.level && filter.level.length > 0) {
        filteredLogs = filteredLogs.filter(log => filter.level!.includes(log.level));
      }

      if (filter.category && filter.category.length > 0) {
        filteredLogs = filteredLogs.filter(log => filter.category!.includes(log.category));
      }

      if (filter.startDate) {
        filteredLogs = filteredLogs.filter(log => log.timestamp >= filter.startDate!.getTime());
      }

      if (filter.endDate) {
        filteredLogs = filteredLogs.filter(log => log.timestamp <= filter.endDate!.getTime());
      }

      if (filter.search) {
        const searchLower = filter.search.toLowerCase();
        filteredLogs = filteredLogs.filter(log =>
          log.action.toLowerCase().includes(searchLower) ||
          log.description.toLowerCase().includes(searchLower)
        );
      }

      if (filter.transactionId) {
        filteredLogs = filteredLogs.filter(log =>
          log.details?.transactionId === filter.transactionId
        );
      }

      if (filter.userId) {
        filteredLogs = filteredLogs.filter(log =>
          log.details?.userId === filter.userId
        );
      }
    }

    return filteredLogs;
  }

  getLogById(id: string): LogEntry | undefined {
    return this.logs.find(log => log.id === id);
  }

  getRecentLogs(count: number = 50): LogEntry[] {
    return this.logs.slice(0, count);
  }

  getLogStats() {
    const last24Hours = Date.now() - (24 * 60 * 60 * 1000);
    const lastWeek = Date.now() - (7 * 24 * 60 * 60 * 1000);

    const recentLogs = this.logs.filter(log => log.timestamp >= last24Hours);
    const weeklyLogs = this.logs.filter(log => log.timestamp >= lastWeek);

    return {
      total: this.logs.length,
      last24Hours: recentLogs.length,
      lastWeek: weeklyLogs.length,
      byLevel: {
        info: this.logs.filter(log => log.level === 'info').length,
        warning: this.logs.filter(log => log.level === 'warning').length,
        error: this.logs.filter(log => log.level === 'error').length,
        success: this.logs.filter(log => log.level === 'success').length,
      },
      byCategory: {
        transaction: this.logs.filter(log => log.category === 'transaction').length,
        user_action: this.logs.filter(log => log.category === 'user_action').length,
        system: this.logs.filter(log => log.category === 'system').length,
        notification: this.logs.filter(log => log.category === 'notification').length,
        error: this.logs.filter(log => log.category === 'error').length,
      },
    };
  }

  // Export/Import methods
  exportLogs(): string {
    return JSON.stringify({
      logs: this.logs,
      exportedAt: Date.now(),
      sessionId: this.sessionId,
      version: '1.0',
    }, null, 2);
  }

  clearLogs() {
    this.logs = [];
    this.saveLogs();

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('action-logs-cleared'));
    }
  }

  // Cleanup old logs
  cleanup(maxAge: number = 30 * 24 * 60 * 60 * 1000) { // 30 days default
    const cutoffTime = Date.now() - maxAge;
    const originalLength = this.logs.length;

    this.logs = this.logs.filter(log => log.timestamp >= cutoffTime);

    if (this.logs.length !== originalLength) {
      this.saveLogs();
      this.info('Log Cleanup', `Removed ${originalLength - this.logs.length} old log entries`, {
        category: 'system',
        details: {
          removed: originalLength - this.logs.length,
          remaining: this.logs.length,
          maxAge,
        },
      });
    }
  }
}

// Singleton instance
export const actionLogger = new ActionLogger();

// Hook for using the logger in React components
export function useActionLogger() {
  return {
    logger: actionLogger,
    info: actionLogger.info.bind(actionLogger),
    warning: actionLogger.warning.bind(actionLogger),
    error: actionLogger.error.bind(actionLogger),
    success: actionLogger.success.bind(actionLogger),
    logTransactionCreate: actionLogger.logTransactionCreate.bind(actionLogger),
    logTransactionUpdate: actionLogger.logTransactionUpdate.bind(actionLogger),
    logTransactionDelete: actionLogger.logTransactionDelete.bind(actionLogger),
    logTransactionError: actionLogger.logTransactionError.bind(actionLogger),
    logNotification: actionLogger.logNotification.bind(actionLogger),
    logHistoryAction: actionLogger.logHistoryAction.bind(actionLogger),
    logPerformance: actionLogger.logPerformance.bind(actionLogger),
    logUserInteraction: actionLogger.logUserInteraction.bind(actionLogger),
    getLogs: actionLogger.getLogs.bind(actionLogger),
    getLogStats: actionLogger.getLogStats.bind(actionLogger),
    exportLogs: actionLogger.exportLogs.bind(actionLogger),
    clearLogs: actionLogger.clearLogs.bind(actionLogger),
  };
}